import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Button, TextField, FormControlLabel, Switch, MenuItem, Select, FormControl, InputLabel, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox } from '@mui/material';
import { cleanCustomerId } from '../../Services/app/operatorservice';

// Yantra Intelligence Configuration master page — SYS_ADMIN / TENANT_ADMIN only.
// Edits the customer-level `ai_config` attribute that drives the auto-Yantra Intelligence cron
// and other Yantra Intelligence features (API key, model, schedule, daily caps, filters).
const AiConfiguration = () => {
    const navigate = useNavigate();
    const customerId = localStorage.getItem('CustomerID');
    const userName = localStorage.getItem('username') || localStorage.getItem('user') || '';
    const authority = (() => {
        try { return JSON.parse(localStorage.getItem('authority')) || ''; }
        catch { return localStorage.getItem('authority') || ''; }
    })();
    const userRoleRaw = (() => {
        try { return JSON.parse(localStorage.getItem('userDetails')) || {}; }
        catch { return {}; }
    })();
    // Scan all likely role fields in the TB user description JSON for a match
    const normalize = (s) => String(s || '').toLowerCase().replace(/[\s_-]+/g, '');
    const roleCandidates = [
        userRoleRaw?.mode, userRoleRaw?.role, userRoleRaw?.userRole, userRoleRaw?.Role,
        userRoleRaw?.description, userRoleRaw?.title,
        localStorage.getItem('role_name'),
    ].map(normalize);
    const isAuthorized =
        authority === 'TENANT_ADMIN' ||
        authority === 'SYS_ADMIN' ||
        roleCandidates.some(v => ['superadmin', 'admin', 'sysadmin', 'tenantadmin'].includes(v));

    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showKey, setShowKey] = useState(false);
    const [showCcKey, setShowCcKey] = useState(false);
    const [keyDirty, setKeyDirty] = useState(false);
    const [ccKeyDirty, setCcKeyDirty] = useState(false);
    const [newModel, setNewModel] = useState('');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null); // { ok, model, latency_ms, sample_response, error }
    const [syncOpen, setSyncOpen] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncCommitting, setSyncCommitting] = useState(false);
    const [syncPreview, setSyncPreview] = useState(null); // { rows, totals }
    const [syncSelected, setSyncSelected] = useState(new Set()); // set of component_number to commit
    const [syncFilter, setSyncFilter] = useState('all'); // 'all' | 'high' | 'medium' | 'low'

    const SERVER = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
    const cid = cleanCustomerId(customerId);

    useEffect(() => {
        if (!isAuthorized) return;
        if (!cid) return;
        setLoading(true);
        axios.get(`${SERVER}/api/buddy/ai-config`, { params: { customer_id: cid } })
            .then(r => setCfg(r.data || null))
            .catch(e => Swal.fire('Error', 'Failed to load config: ' + (e?.response?.data?.message || e?.message), 'error'))
            .finally(() => setLoading(false));
    }, [cid, SERVER, isAuthorized]);

    if (!isAuthorized) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
                <h3 style={{ color: '#374151' }}>Forbidden</h3>
                <p style={{ color: '#6b7280' }}>Yantra Intelligence Configuration is restricted to Super Admin users only.</p>
                <p style={{ color: '#9ca3af', fontSize: 12 }}>Your role: <code>{authority || 'unknown'}</code></p>
                <Button onClick={() => navigate('/')} variant="contained" sx={{ marginTop: 2 }}>Go Home</Button>
            </div>
        );
    }

    if (loading || !cfg) {
        return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading Yantra Intelligence configuration…</div>;
    }

    const update = (field, value) => setCfg(prev => ({ ...prev, [field]: value }));

    const save = async () => {
        setSaving(true);
        try {
            const toSend = { ...cfg };
            // If user didn't touch the masked keys, don't send them (backend preserves existing)
            if (!keyDirty && typeof toSend.anthropic_api_key === 'string' && toSend.anthropic_api_key.startsWith('••••••')) {
                delete toSend.anthropic_api_key;
            }
            if (!ccKeyDirty && typeof toSend.claude_code_key === 'string' && toSend.claude_code_key.startsWith('••••••')) {
                delete toSend.claude_code_key;
            }
            delete toSend.key_source;
            delete toSend.env_key_available;
            await axios.post(`${SERVER}/api/buddy/ai-config`, {
                customer_id: cid,
                updated_by: userName,
                config: toSend,
            });
            Swal.fire({ title: 'Saved', text: 'Yantra Intelligence configuration updated.', icon: 'success', timer: 1800, showConfirmButton: false });
            const r = await axios.get(`${SERVER}/api/buddy/ai-config`, { params: { customer_id: cid } });
            setCfg(r.data);
            setKeyDirty(false);
            setCcKeyDirty(false);
            setShowKey(false);
            setShowCcKey(false);
        } catch (e) {
            Swal.fire('Save failed', e?.response?.data?.message || e?.message || 'Unknown error', 'error');
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            // Send whatever's currently in the form (not what's saved). Backend
            // swaps in saved values if masked sentinel detected.
            const r = await axios.post(`${SERVER}/api/buddy/ai-config/test`, {
                customer_id: cid,
                auth_mode: cfg.auth_mode || 'api_key',
                anthropic_api_key: cfg.anthropic_api_key || '',
                claude_code_key: cfg.claude_code_key || '',
                anthropic_model: cfg.anthropic_model || '',
            });
            setTestResult(r.data);
        } catch (e) {
            setTestResult({
                ok: false,
                error: e?.response?.data?.message || e?.message || 'Request failed',
                auth_mode: cfg.auth_mode || 'api_key',
                model: cfg.anthropic_model || '',
            });
        } finally {
            setTesting(false);
        }
    };

    const openSyncDialog = async () => {
        setSyncOpen(true);
        setSyncPreview(null);
        setSyncSelected(new Set());
        setSyncLoading(true);
        try {
            const r = await axios.get(`${SERVER}/api/buddy/ai-config/program-backfill/preview`, {
                params: { customer_id: cid, days: 30 },
            });
            setSyncPreview(r.data);
            // Default-select high + medium confidence (safer); leave low unchecked
            const auto = new Set();
            (r.data.rows || []).forEach(row => {
                if (row.confidence === 'high' || row.confidence === 'medium') {
                    auto.add(row.component_number);
                }
            });
            setSyncSelected(auto);
        } catch (e) {
            Swal.fire('Preview failed', e?.response?.data?.message || e?.message || 'Unknown error', 'error');
            setSyncOpen(false);
        } finally {
            setSyncLoading(false);
        }
    };
    const toggleSyncRow = (compNum) => {
        const next = new Set(syncSelected);
        if (next.has(compNum)) next.delete(compNum); else next.add(compNum);
        setSyncSelected(next);
    };
    const visibleSyncRows = (syncPreview?.rows || []).filter(r =>
        syncFilter === 'all' || r.confidence === syncFilter
    );
    const toggleSyncAll = () => {
        if (visibleSyncRows.every(r => syncSelected.has(r.component_number))) {
            const next = new Set(syncSelected);
            visibleSyncRows.forEach(r => next.delete(r.component_number));
            setSyncSelected(next);
        } else {
            const next = new Set(syncSelected);
            visibleSyncRows.forEach(r => next.add(r.component_number));
            setSyncSelected(next);
        }
    };
    const commitSync = async () => {
        if (syncSelected.size === 0) { Swal.fire({ icon: 'info', title: 'Nothing selected', timer: 1100, showConfirmButton: false }); return; }
        const updates = (syncPreview?.rows || [])
            .filter(r => syncSelected.has(r.component_number))
            .map(r => ({ component_number: r.component_number, program_no: r.proposed_program_no }));
        setSyncCommitting(true);
        try {
            const r = await axios.post(`${SERVER}/api/buddy/ai-config/program-backfill/commit`, {
                customer_id: cid, updates,
            });
            Swal.fire({ icon: 'success', title: `Synced ${r.data.updated} program numbers`, text: r.data.skipped ? `${r.data.skipped} already up-to-date` : '', timer: 2200, showConfirmButton: false });
            setSyncOpen(false);
        } catch (e) {
            Swal.fire('Commit failed', e?.response?.data?.message || e?.message || 'Unknown error', 'error');
        } finally {
            setSyncCommitting(false);
        }
    };

    const addModel = () => {
        const m = (newModel || '').trim();
        if (!m) return;
        const list = Array.isArray(cfg.available_models) ? cfg.available_models : [];
        if (list.includes(m)) { Swal.fire({ icon: 'info', title: 'Already in list', timer: 1200, showConfirmButton: false }); return; }
        update('available_models', [...list, m]);
        setNewModel('');
    };
    const removeModel = (m) => {
        const list = (cfg.available_models || []).filter(x => x !== m);
        update('available_models', list);
        if (cfg.anthropic_model === m && list.length > 0) update('anthropic_model', list[0]);
    };

    const sectionStyle = { background: 'var(--y-bg, #fff)', borderRadius: 8, padding: 20, marginBottom: 16, border: '1px solid var(--y-border, #e5e7eb)' };
    const sectionTitle = { fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 };
    const rowStyle = { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14, alignItems: 'center', marginBottom: 14 };
    const labelStyle = { fontSize: 13, color: '#374151', fontWeight: 500 };
    const helpStyle = { fontSize: 11, color: '#9ca3af', marginTop: 4 };

    return (
        <div style={{ padding: '20px 28px 40px', background: 'var(--y-bg-soft, #fafafa)', minHeight: 'calc(100vh - 70px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, color: '#111827', fontWeight: 700 }}>
                        ⚙️ Yantra Intelligence Configuration
                    </h2>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        Provider keys, auto-optimization schedule, daily caps, smart filters
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="outlined" onClick={() => navigate(-1)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="outlined"
                        onClick={openSyncDialog}
                        sx={{ textTransform: 'none', borderColor: '#0891b2', color: '#0891b2', ':hover': { borderColor: '#0e7490', background: '#ecfeff' } }}
                    >
                        🔄 Sync Program Numbers
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={testConnection}
                        disabled={testing}
                        sx={{ textTransform: 'none', borderColor: '#4338ca', color: '#4338ca', ':hover': { borderColor: '#3730a3', background: '#eef2ff' } }}
                    >
                        {testing ? 'Testing…' : '🧪 Test Connection'}
                    </Button>
                    <Button variant="contained" onClick={save} disabled={saving} sx={{ backgroundColor: '#10b981', textTransform: 'none', ':hover': { backgroundColor: '#059669' } }}>
                        {saving ? 'Saving…' : '💾 Save Configuration'}
                    </Button>
                </div>
            </div>

            {/* Test result banner */}
            {testResult && (
                <div style={{
                    background: testResult.ok ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${testResult.ok ? '#10b981' : '#ef4444'}`,
                    borderRadius: 8, padding: '12px 16px', marginBottom: 14,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
                }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: testResult.ok ? '#047857' : '#b91c1c', marginBottom: 4 }}>
                            {testResult.ok ? '✓ Connected' : '✗ Connection failed'}
                        </div>
                        <div style={{ fontSize: 12, color: '#374151' }}>
                            Mode: <code>{testResult.auth_mode}</code>
                            {' · '}Model: <code>{testResult.model}</code>
                            {testResult.latency_ms != null && (<> {' · '}Latency: <b>{testResult.latency_ms} ms</b></>)}
                        </div>
                        {testResult.ok && testResult.sample_response && (
                            <div style={{ fontSize: 12, color: '#065f46', marginTop: 6 }}>
                                Sample response: <code style={{ background: '#d1fae5', padding: '1px 6px', borderRadius: 3 }}>{testResult.sample_response}</code>
                            </div>
                        )}
                        {!testResult.ok && testResult.error && (
                            <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6, fontFamily: 'monospace', wordBreak: 'break-word' }}>
                                {testResult.error}
                            </div>
                        )}
                    </div>
                    <button onClick={() => setTestResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 16, padding: 2 }}>✕</button>
                </div>
            )}

            {cfg.updated_at > 0 && (
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
                    Last updated by <b>{cfg.updated_by || 'unknown'}</b> at {new Date(cfg.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </div>
            )}

            {/* PROVIDER & AUTH */}
            <div style={sectionStyle}>
                <div style={sectionTitle}>🔑 Provider & Auth</div>

                {/* Auth mode selector */}
                <div style={rowStyle}>
                    <div style={labelStyle}>
                        Auth Method
                        <div style={helpStyle}>How to authenticate Yantra Intelligence calls</div>
                    </div>
                    <FormControl size="small" sx={{ minWidth: 320 }}>
                        <Select value={cfg.auth_mode || 'api_key'} onChange={e => update('auth_mode', e.target.value)}>
                            <MenuItem value="api_key">Anthropic API Key (direct billing, pay-per-token)</MenuItem>
                            <MenuItem value="claude_code">Claude Code Key (subscription / CLI session token)</MenuItem>
                        </Select>
                    </FormControl>
                </div>

                {/* API key (only shown when auth_mode = api_key) */}
                {(cfg.auth_mode || 'api_key') === 'api_key' && (
                    <div style={rowStyle}>
                        <div style={labelStyle}>
                            Anthropic API Key
                            <div style={helpStyle}>
                                Source: <Chip size="small" label={cfg.key_source} sx={{ height: 18, fontSize: 10, marginLeft: 0.5 }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                type={showKey ? 'text' : 'password'}
                                value={cfg.anthropic_api_key || ''}
                                onChange={e => { update('anthropic_api_key', e.target.value); setKeyDirty(true); }}
                                placeholder={cfg.env_key_available ? '(empty = use env ANTHROPIC_API_KEY)' : 'sk-ant-...'}
                                sx={{ flex: 1 }}
                                InputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                            />
                            <Button size="small" variant="outlined" onClick={() => setShowKey(s => !s)} sx={{ textTransform: 'none' }}>
                                {showKey ? 'Hide' : 'Show'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Claude Code key (only shown when auth_mode = claude_code) */}
                {cfg.auth_mode === 'claude_code' && (
                    <div style={rowStyle}>
                        <div style={labelStyle}>
                            Claude Code Key
                            <div style={helpStyle}>
                                Subscription / CLI session token. Source: <Chip size="small" label={cfg.key_source} sx={{ height: 18, fontSize: 10, marginLeft: 0.5 }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                type={showCcKey ? 'text' : 'password'}
                                value={cfg.claude_code_key || ''}
                                onChange={e => { update('claude_code_key', e.target.value); setCcKeyDirty(true); }}
                                placeholder="cc-..."
                                sx={{ flex: 1 }}
                                InputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                            />
                            <Button size="small" variant="outlined" onClick={() => setShowCcKey(s => !s)} sx={{ textTransform: 'none' }}>
                                {showCcKey ? 'Hide' : 'Show'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Active Model — populated dynamically from available_models */}
                <div style={rowStyle}>
                    <div style={labelStyle}>
                        Active Model
                        <div style={helpStyle}>Pick from the list below; admin-editable</div>
                    </div>
                    <FormControl size="small" sx={{ minWidth: 300 }}>
                        <Select
                            value={cfg.anthropic_model || (cfg.available_models?.[0] || '')}
                            onChange={e => update('anthropic_model', e.target.value)}
                        >
                            {(cfg.available_models || []).map(m => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                            {(cfg.available_models || []).length === 0 && (
                                <MenuItem disabled value="">No models — add one below</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </div>

                {/* Available Models — dynamic master list */}
                <div style={rowStyle}>
                    <div style={labelStyle}>
                        Available Models
                        <div style={helpStyle}>Add/remove without code changes — drives the dropdown above</div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                            {(cfg.available_models || []).map(m => (
                                <Chip
                                    key={m}
                                    label={m}
                                    onDelete={() => removeModel(m)}
                                    size="small"
                                    sx={{ fontFamily: 'monospace', fontSize: 11, background: cfg.anthropic_model === m ? '#ecfeff' : '#f3f4f6', borderColor: cfg.anthropic_model === m ? '#06b6d4' : '#e5e7eb', border: '1px solid' }}
                                />
                            ))}
                            {(cfg.available_models || []).length === 0 && (
                                <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>(no models yet)</span>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <TextField
                                size="small"
                                value={newModel}
                                onChange={e => setNewModel(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addModel(); } }}
                                placeholder="e.g. claude-opus-4-8"
                                sx={{ flex: 1, maxWidth: 320 }}
                                InputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                            />
                            <Button size="small" variant="outlined" onClick={addModel} sx={{ textTransform: 'none' }}>+ Add Model</Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* SCHEDULE & LIMITS */}
            <div style={sectionStyle}>
                <div style={sectionTitle}>⏰ Auto-Yantra Intelligence Schedule & Limits</div>
                <div style={rowStyle}>
                    <div style={labelStyle}>
                        Enable Auto-Yantra Intelligence
                        <div style={helpStyle}>Master switch for background auto-optimization</div>
                    </div>
                    <FormControlLabel control={<Switch checked={!!cfg.auto_ai_enabled} onChange={e => update('auto_ai_enabled', e.target.checked)} />} label={cfg.auto_ai_enabled ? 'Enabled' : 'Disabled'} />
                </div>
                <div style={rowStyle}>
                    <div style={labelStyle}>
                        Cron Schedule
                        <div style={helpStyle}>Standard cron expression (default: every 10 min)</div>
                    </div>
                    <TextField size="small" value={cfg.cron_schedule || ''} onChange={e => update('cron_schedule', e.target.value)} sx={{ width: 220 }} InputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} placeholder="*/10 * * * *" />
                </div>
                <div style={rowStyle}>
                    <div style={labelStyle}>
                        Max Calls / Day
                        <div style={helpStyle}>Hard cap on Yantra Intelligence calls per day for this customer</div>
                    </div>
                    <TextField size="small" type="number" value={cfg.max_calls_per_day_per_customer ?? 20} onChange={e => update('max_calls_per_day_per_customer', parseInt(e.target.value, 10) || 0)} sx={{ width: 120 }} />
                </div>
            </div>

            {/* SMART FILTERS */}
            <div style={sectionStyle}>
                <div style={sectionTitle}>🎯 Smart Filters</div>
                <div style={rowStyle}>
                    <div style={labelStyle}>
                        Min Program Size (bytes)
                        <div style={helpStyle}>Skip programs smaller than this — too short to be worth optimizing</div>
                    </div>
                    <TextField size="small" type="number" value={cfg.min_program_size_bytes ?? 500} onChange={e => update('min_program_size_bytes', parseInt(e.target.value, 10) || 0)} sx={{ width: 120 }} />
                </div>
                <div style={rowStyle}>
                    <div style={labelStyle}>
                        Skip if Savings &lt; (%)
                        <div style={helpStyle}>Don't save Yantra Intelligence output if projected savings are below this threshold</div>
                    </div>
                    <TextField size="small" type="number" value={cfg.skip_if_under_pct_savings ?? 5} onChange={e => update('skip_if_under_pct_savings', parseFloat(e.target.value) || 0)} sx={{ width: 120 }} />
                </div>
            </div>

            {/* SYNC PROGRAM NUMBERS dialog */}
            <Dialog open={syncOpen} onClose={() => setSyncOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    🔄 Sync Program Numbers from Telemetry
                    {syncPreview && (
                        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginTop: 4 }}>
                            {syncPreview.totals.master_components} components in master · {syncPreview.totals.with_existing} already have program_no · <b>{syncPreview.totals.proposed_updates} proposed updates</b>
                        </div>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {syncLoading && <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>Analyzing 30 days of telemetry…</div>}
                    {!syncLoading && syncPreview && (
                        <>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                                <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Filter:</span>
                                {['all', 'high', 'medium', 'low'].map(f => (
                                    <Chip
                                        key={f}
                                        label={f === 'all' ? `All (${syncPreview.rows.length})` : `${f} (${syncPreview.rows.filter(r => r.confidence === f).length})`}
                                        size="small"
                                        onClick={() => setSyncFilter(f)}
                                        color={syncFilter === f ? 'primary' : 'default'}
                                        variant={syncFilter === f ? 'filled' : 'outlined'}
                                        sx={{ textTransform: 'capitalize' }}
                                    />
                                ))}
                                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
                                    {syncSelected.size} selected
                                </span>
                            </div>
                            <div style={{ maxHeight: 460, overflow: 'auto', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 6 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead style={{ position: 'sticky', top: 0, background: 'var(--y-bg-soft, #f9fafb)', zIndex: 1 }}>
                                        <tr>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--y-border, #e5e7eb)' }}>
                                                <Checkbox
                                                    size="small"
                                                    checked={visibleSyncRows.length > 0 && visibleSyncRows.every(r => syncSelected.has(r.component_number))}
                                                    indeterminate={visibleSyncRows.some(r => syncSelected.has(r.component_number)) && !visibleSyncRows.every(r => syncSelected.has(r.component_number))}
                                                    onChange={toggleSyncAll}
                                                />
                                            </th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--y-border, #e5e7eb)', fontSize: 11, fontWeight: 700, color: '#374151' }}>Code</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--y-border, #e5e7eb)', fontSize: 11, fontWeight: 700, color: '#374151' }}>Component</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--y-border, #e5e7eb)', fontSize: 11, fontWeight: 700, color: '#374151' }}>Current</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--y-border, #e5e7eb)', fontSize: 11, fontWeight: 700, color: '#374151' }}>Proposed</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--y-border, #e5e7eb)', fontSize: 11, fontWeight: 700, color: '#374151' }}>Source</th>
                                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--y-border, #e5e7eb)', fontSize: 11, fontWeight: 700, color: '#374151' }}>Confidence</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleSyncRows.length === 0 && (
                                            <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#9ca3af' }}>No rows match the filter</td></tr>
                                        )}
                                        {visibleSyncRows.map(row => (
                                            <tr key={row.component_number} style={{ background: syncSelected.has(row.component_number) ? '#eff6ff' : 'transparent' }}>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6' }}>
                                                    <Checkbox size="small" checked={syncSelected.has(row.component_number)} onChange={() => toggleSyncRow(row.component_number)} />
                                                </td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, fontFamily: 'monospace' }}>{row.component_number}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.component_name}>{row.component_name}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6', color: '#9ca3af', fontFamily: 'monospace' }}>{row.current_program_no || '—'}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6', color: '#7c3aed', fontWeight: 700, fontFamily: 'monospace' }}>{row.proposed_program_no}</td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6' }}>
                                                    <Chip
                                                        size="small"
                                                        label={row.source === 'both' ? 'telemetry + name' : row.source.replace('_', ' ')}
                                                        sx={{ height: 18, fontSize: 10 }}
                                                    />
                                                    {row.observations > 0 && <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 6 }}>{row.observations} obs</span>}
                                                </td>
                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6' }}>
                                                    <Chip
                                                        size="small"
                                                        label={row.confidence}
                                                        sx={{
                                                            height: 18, fontSize: 10, textTransform: 'capitalize',
                                                            background: row.confidence === 'high' ? '#d1fae5' : (row.confidence === 'medium' ? '#fef3c7' : '#fee2e2'),
                                                            color: row.confidence === 'high' ? '#065f46' : (row.confidence === 'medium' ? '#92400e' : '#991b1b'),
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSyncOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={commitSync}
                        disabled={syncCommitting || syncSelected.size === 0}
                        sx={{ backgroundColor: '#10b981', textTransform: 'none', ':hover': { backgroundColor: '#059669' } }}
                    >
                        {syncCommitting ? 'Committing…' : `💾 Commit ${syncSelected.size} updates`}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AiConfiguration;
